import React from 'react';
import { Button, Space, Tag, Typography } from 'antd';

import { DataBadge } from './DataCoverage';
import {
  CHECK_STATUS_COLOURS,
  movesLine,
  overlapLine,
  plural,
  windowLine,
} from './format';
import { ProposalCard as ProposalCardData } from './types';

const STATUS_COLOURS: Record<ProposalCardData['status'], string | undefined> = {
  open: 'processing',
  saved: 'success',
  rejected: 'default',
};

type Props = {
  card: ProposalCardData;
  /** The version the message that shows this card produced. */
  versionId: number;
  onOpen: (versionId: number) => void;
  onSave: (card: ProposalCardData) => void;
  onReject: (card: ProposalCardData) => void;
  onAsk: (card: ProposalCardData) => void;
};

/**
 * One proposal, as the chat shows it. The content is the proposal's CURRENT
 * state (`ThreadDetail.proposals`); Open shows the version the message made,
 * and Save saves the latest version. Only an open proposal can be saved or
 * rejected. Save stays enabled on a card with blocking data gaps: the save
 * dialog is where the blockers and their remedies are read.
 */
const ProposalCard: React.FC<Props> = ({
  card,
  versionId,
  onOpen,
  onSave,
  onReject,
  onAsk,
}) => {
  const open = card.status === 'open';
  return (
    <article
      className="blueprint-builder__card"
      aria-label={`Proposal ${card.title}`}
    >
      <header className="blueprint-builder__card-head">
        <Typography.Text strong className="blueprint-builder__card-title">
          {card.title}
        </Typography.Text>
        {card.character && <Tag color="purple">{card.character}</Tag>}
      </header>
      <div className="blueprint-builder__card-line">
        {windowLine(card.window)}
      </div>
      {card.moves.length > 0 && (
        <div className="blueprint-builder__card-line blueprint-builder__card-moves">
          {movesLine(card.moves)}
        </div>
      )}
      <div className="blueprint-builder__card-line">
        {plural(card.news_total, 'news item')} · {overlapLine(card.overlap_max)}
      </div>
      <Space size={[4, 4]} wrap className="blueprint-builder__card-tags">
        <Tag color={CHECK_STATUS_COLOURS[card.check_status]}>
          checks {card.check_status.toUpperCase()}
        </Tag>
        {/* Guarded here too: Space would keep an empty slot for a null badge. */}
        {card.data_status && (
          <DataBadge
            status={card.data_status}
            gapCount={card.gap_count}
            blockers={card.blockers}
          />
        )}
        <Tag color={STATUS_COLOURS[card.status]}>{card.status}</Tag>
        {card.version_count > 1 && (
          <Tag>{plural(card.version_count, 'version')}</Tag>
        )}
      </Space>
      <Space size={[6, 6]} wrap className="blueprint-builder__card-actions">
        <Button size="small" type="primary" onClick={() => onOpen(versionId)}>
          Open
        </Button>
        <Button size="small" disabled={!open} onClick={() => onSave(card)}>
          Save
        </Button>
        <Button
          size="small"
          danger
          disabled={!open}
          onClick={() => onReject(card)}
        >
          Reject
        </Button>
        <Button size="small" type="text" onClick={() => onAsk(card)}>
          Ask
        </Button>
      </Space>
    </article>
  );
};

export default ProposalCard;
